// ─── Slot registry (PLATFORM_SDK §2 — the host side of UI contribution points) ──────────
//
// A single in-memory registry of every SlotContribution. Internal plugins register at module
// load; the host adds external/declarative contributions after fetching /api/plugins (C3). The
// host renders a slot by asking getSlotContributions(slot, ctx) — it never hardcodes which
// widgets live where. Empty registry = nothing extra renders (exactly today's behaviour).

import type { SlotContribution, SlotId, SlotContext, SlotCondition } from './types'

const REGISTRY: SlotContribution[] = []

/** Register (or replace, by id) a slot contribution. Idempotent — re-registering an id updates it,
 *  so a re-fetch of /api/plugins or a hot reload doesn't duplicate widgets. */
export function registerSlot(contribution: SlotContribution): void {
  const i = REGISTRY.findIndex((c) => c.id === contribution.id)
  if (i >= 0) REGISTRY[i] = contribution
  else REGISTRY.push(contribution)
}

/** Remove contributions: all of them (no arg) or those matching a predicate (e.g. a plugin id
 *  prefix when an extension is disabled). Used by the host to re-sync external contributions. */
export function clearSlots(predicate?: (c: SlotContribution) => boolean): void {
  if (!predicate) {
    REGISTRY.length = 0
    return
  }
  for (let i = REGISTRY.length - 1; i >= 0; i--) {
    if (predicate(REGISTRY[i])) REGISTRY.splice(i, 1)
  }
}

/** Evaluate a contribution's `when` against the live context. Fixed vocabulary only. */
export function matchesCondition(when: SlotCondition | undefined, ctx: SlotContext): boolean {
  if (!when || when === 'always') return true
  if ('paneState' in when) return ctx.paneState === when.paneState
  if ('agent' in when) return ctx.agent === when.agent
  return true
}

/** Every contribution for `slot` whose condition matches, sorted by `order` (asc, default 0). */
export function getSlotContributions(slot: SlotId, ctx: SlotContext): SlotContribution[] {
  return REGISTRY.filter((c) => c.slot === slot && matchesCondition(c.when, ctx)).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  )
}

/** Snapshot of every registered contribution (debug / tests). */
export function allSlotContributions(): readonly SlotContribution[] {
  return REGISTRY
}

export type { SlotContribution, SlotId, SlotContext, SlotCondition } from './types'
